// Katalog metriği penceresi (oluştur/düzenle — tek modal, kullanıcı kararı 2026-10-08): etiket, kod
// (benzersizlik canlı — kural 15), üst kategori, açıklama, birim (+ alternatifler, kullanım kapısı DR10 —
// MetricUnitSection; taslak modeli lib/kpi-metric-draft), değer tipi, türetilmiş + varsayılan
// formüller (FormulaEditor katalog modu), toplama, değişim/artış varsayılanları, form sorusu kodu,
// gereksinimler. Kullanımdan kaldır → ConfirmDialog → sonuç üst bileşende (rev-B 11: setteyse liste).
// Kırılım (spec §6.4 DR14): "Konum" radyosu (MetricModalLocation) — üye kipinde birim üstten
// (alan yerine salt okunur satır); grup düğümü (`kind="group"`) tamlık/kural düzenler; toplamda
// "Kırılım" paneli. "Yeni grup…" / "+ Kırılım grubu ekle" → `onNewGroup` (üst bileşen taslağı saklar).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  KPI_AGGREGATIONS,
  KPI_AGGREGATION_LABELS,
  KPI_VALUE_TYPES,
  slugifyKpiCode,
  type KpiAggregation,
  type KpiMetric,
  type KpiValueType,
} from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import {
  catalogBreakdownView,
  catalogPathOf,
  locationGate,
  locationModeOf,
  type LocationMode,
} from "../../lib/kpi-catalog-breakdown";
import { type MetricDraft } from "../../lib/kpi-metric-draft";
import { buildIndicatorTree } from "../../lib/kpi-set-editor";
import { KPI_COPY, kpiSetManagementHref } from "../../lib/kpi-ui";
import { buildChildrenByCode } from "../../lib/kpi-wavg-pairs";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { AggregationHint } from "./AggregationHint";
import { AggregationWeightField } from "./AggregationWeightField";
import { FormulaEditor, type FormulaServerError } from "./FormulaEditor";
import { KpiModal } from "./KpiModal";
import { MetricErrorText } from "./MetricInUseNotice";
import { MetricLabelsSection } from "./MetricLabelsSection";
import { GroupMetaSection, TotalBreakdownPanel } from "./MetricModalBreakdown";
import { MetricModalDefaults } from "./MetricModalDefaults";
import { MetricModalLocation } from "./MetricModalLocation";
import { MetricRequirementsField } from "./MetricRequirementsField";
import { MetricUnitSection } from "./MetricUnitSection";
import {
  btnDanger,
  btnGhost,
  btnPrimary,
  errorBox,
  fieldStyle,
  inputStyle,
  labelStyle,
  monoStyle,
} from "./kpi-form-styles";

const AGGREGATION_LABELS = KPI_AGGREGATION_LABELS;
const VALUE_TYPE_LABELS: Record<KpiValueType, string> = {
  number: "Sayı",
  percent: "Yüzde",
  text: "Metin",
};

export interface MetricModalProps {
  metric: KpiMetric | null;
  initialDraft: MetricDraft;
  /** tüm katalog (üst seçenekleri + kod benzersizliği + formül kodları) */
  catalog: KpiMetric[];
  canEdit: boolean;
  busy: boolean;
  error: FormulaServerError | null;
  onSave: (draft: MetricDraft) => void;
  onDeprecate: () => void;
  onCancel: () => void;
  /** category: yalnız etiket/kod/üst/açıklama (diğer alanlar göstergeye aittir; rev. madde 12);
   *  group: kırılım grubu — etiket/kod/açıklama + tamlık/kural. */
  kind?: "metric" | "category" | "group";
  /** kök konumu (kök = kategori) — `kpi.catalog.categories` */
  canManageCategories?: boolean | undefined;
  /** "Yeni grup…" / "+ Kırılım grubu ekle": toplam için BreakdownGroupModal (taslak üstte saklanır) */
  onNewGroup?:
    | ((totalId: string, draft: MetricDraft, origin: "location" | "panel") => void)
    | undefined;
  lockedHint?: string | undefined; // salt okunur not, başlık altında (rev. madde 12 kategori)
}

export function MetricModal(props: MetricModalProps): JSX.Element {
  const { metric, initialDraft, catalog, canEdit, busy, error, onSave, onDeprecate, onCancel } =
    props;
  const [draft, setDraft] = useState<MetricDraft>(initialDraft);
  const [confirmDeprecate, setConfirmDeprecate] = useState(false);
  const [unitBlocked, setUnitBlocked] = useState(false);
  /** "Veri Yönetimi'nde aç" — kaydedilmemiş taslak varken önce vazgeç onayı (KpiModal dirty kalıbı) */
  const [pendingClient, setPendingClient] = useState<{
    clientId: string;
    setId: string | null;
  } | null>(null);
  const navigate = useNavigate();
  const [locMode, setLocMode] = useState<LocationMode>(() => locationModeOf(initialDraft));
  useEffect(() => {
    setDraft(initialDraft);
    setLocMode(locationModeOf(initialDraft));
  }, [initialDraft]);

  const isNew = metric === null;
  const isGroup = props.kind === "group";
  // grup düğümü kategori gibi dar pencere: birim/formül/toplama göstergeye aittir
  const isCategory = props.kind === "category" || isGroup;
  const dis = !canEdit || busy;
  const view = useMemo(() => catalogBreakdownView(catalog), [catalog]);
  const memberMode = !isCategory && locMode === "member";
  const rootAllowed = props.kind === "category" || props.canManageCategories === true;
  const tree = useMemo(() => buildIndicatorTree(catalog), [catalog]);
  const allCodes = useMemo(() => new Set(catalog.map((m) => m.code)), [catalog]);
  const labelsByCode = useMemo(() => new Map(catalog.map((m) => [m.code, m.label])), [catalog]);
  // TODO-KPI-91: AGIRLIKLI_ORT eşleme önizlemesi (katalog sırası; yalnız etkin — sunucu kapısı gibi)
  const childrenByCode = useMemo(
    () => buildChildrenByCode(catalog.filter((m) => m.status === "active")),
    [catalog],
  );
  const byId = useMemo(() => new Map(catalog.map((m) => [m.id, m])), [catalog]);
  // Başlık altı konum: üst kategori zinciri ("Çevresel › Enerji Yönetimi")
  const parentPath = catalogPathOf(byId, draft.parentId) || "Kök (ana kategori)";
  const totalOfMember =
    memberMode && draft.parentId ? byId.get(byId.get(draft.parentId)?.parentId ?? "") : undefined;
  const L = KPI_BREAKDOWN_COPY.location;
  // yerinde düzenleme konum değişikliği değil: üst/üye değişmediyse kapı koşmaz (ör. müşteriye
  // özel kökteki eski yaprak, kategori izni olmadan da yeniden adlandırılabilir)
  const locationUnchanged =
    metric !== null &&
    draft.parentId === metric.parentId &&
    draft.dimensionMember === (metric.dimensionMember ?? null);
  const locationBlock =
    isGroup || locationUnchanged
      ? null
      : locationGate({
          mode: locMode,
          parentId: draft.parentId,
          dimensionMember: draft.dimensionMember,
          view,
          rootAllowed,
          copy: {
            needGroup: L.needGroup,
            memberSlugInvalid: L.memberSlugInvalid,
            needCategory: L.needCategory,
            rootLocked: L.rootLocked,
          },
        });
  const unitsByCode = useMemo(
    () => new Map(catalog.map((m) => [m.code, m.defaultUnit] as const)),
    [catalog],
  );

  // TODO-KPI-32: ağırlık adayları — yaprak (kategori olmayan), metin olmayan, aktif, kendisi hariç
  const weightOptions = useMemo(
    () =>
      catalog
        .filter(
          (m) =>
            m.id !== metric?.id &&
            m.status === "active" &&
            m.valueType !== "text" &&
            (tree.childrenOf.get(m.id) ?? []).length === 0,
        )
        .map((m) => ({ value: m.id, label: m.label, code: m.code, hint: m.defaultUnit ?? "" })),
    [catalog, metric, tree],
  );
  const weightMissing =
    !isCategory && draft.aggregation === "weighted_avg" && !draft.aggregationWeightMetricId;

  const codeTaken =
    draft.code.trim() !== "" &&
    draft.code.trim() !== metric?.code &&
    allCodes.has(draft.code.trim());
  const codePreview =
    draft.code.trim() || (draft.label ? slugifyKpiCode(draft.label, allCodes) : "");
  const activeFormulaId = draft.defaultFormulas.find((f) => f.isActive)?.id ?? null;
  const formulasOk =
    !draft.isDerived || (draft.defaultFormulas.length > 0 && activeFormulaId !== null);
  const canSave =
    canEdit &&
    !busy &&
    draft.label.trim() !== "" &&
    !codeTaken &&
    formulasOk &&
    !weightMissing &&
    locationBlock === null &&
    (isCategory || memberMode || !unitBlocked);

  const isDirty = canEdit && !busy && JSON.stringify(draft) !== JSON.stringify(initialDraft);

  const set = useCallback(<K extends keyof MetricDraft>(k: K, v: MetricDraft[K]): void => {
    setDraft((d) => ({ ...d, [k]: v }));
  }, []);

  /** yeni üye kodu boşsa grup modalıyla aynı kalıp: `<toplam>_<üye>` */
  function finalDraft(): MetricDraft {
    if (!isNew || !memberMode || draft.code.trim() !== "" || !totalOfMember) return draft;
    return {
      ...draft,
      code: slugifyKpiCode(`${totalOfMember.code}_${draft.dimensionMember ?? ""}`, allCodes),
    };
  }

  const kicker = isGroup
    ? KPI_BREAKDOWN_COPY.catalog.groupKicker
    : isCategory
      ? isNew
        ? "Yeni katalog kategorisi"
        : "Katalog kategorisi"
      : isNew
        ? "Yeni katalog göstergesi"
        : "Katalog göstergesi";
  const title = isNew
    ? draft.label || (isCategory ? "Yeni kategori" : "Yeni gösterge")
    : metric.label;
  const description = `${isNew ? "Üst: " : ""}${parentPath}${
    metric?.status === "deprecated"
      ? " · Kullanımdan kalktı — veri setine eklenemez, değer girilemez."
      : ""
  }${props.lockedHint ? ` · ${props.lockedHint}` : ""}`;
  const footer = canEdit ? (
    <>
      {!isNew && metric.status === "active" ? (
        <button
          type="button"
          style={{ ...btnDanger, marginRight: "auto" }}
          disabled={busy}
          onClick={() => setConfirmDeprecate(true)}
        >
          Kullanımdan kaldır
        </button>
      ) : null}
      <button type="button" style={btnGhost} onClick={onCancel} disabled={busy}>
        Vazgeç
      </button>
      <button
        type="submit"
        form="m-form"
        style={{ ...btnPrimary, opacity: canSave ? 1 : 0.5 }}
        disabled={!canSave}
        aria-busy={busy || undefined}
      >
        {busy ? "Kaydediliyor…" : isNew ? "Oluştur" : "Kaydet"}
      </button>
    </>
  ) : (
    <button type="button" style={btnGhost} onClick={onCancel}>
      Kapat
    </button>
  );

  return (
    <KpiModal
      open
      kicker={kicker}
      title={title}
      description={description}
      width={isCategory ? 560 : 1060}
      onClose={onCancel}
      // güvenlik/UX taraması 2026-10-08 (U-M2): taslak başlangıçtan farklıysa Esc/✕/arka plan onay ister
      dirty={isDirty}
      footer={footer}
    >
      <form
        id="m-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) onSave(finalDraft());
        }}
        className="grid gap-x-6"
        style={{
          gridTemplateColumns: isCategory ? "1fr" : "repeat(auto-fit, minmax(380px, 1fr))",
          alignItems: "start",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="m-label">
              Etiket
            </label>
            <input
              id="m-label"
              style={inputStyle}
              value={draft.label}
              disabled={dis}
              required
              maxLength={200}
              onChange={(e) => set("label", e.target.value)}
            />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="m-code">
              Kod {isNew ? "(boşsa etiketten üretilir)" : ""}
            </label>
            <input
              id="m-code"
              style={{
                ...inputStyle,
                ...monoStyle,
                ...(codeTaken ? { borderColor: "var(--color-error)" } : {}),
              }}
              value={draft.code}
              placeholder={codePreview}
              disabled={dis}
              maxLength={64}
              spellCheck={false}
              aria-invalid={codeTaken || undefined}
              onChange={(e) => set("code", e.target.value)}
            />
            {codeTaken ? (
              <div className="text-xs" style={{ color: "var(--color-error)" }}>
                Bu kod katalogda zaten var.
              </div>
            ) : !isNew && draft.code.trim() !== "" && draft.code.trim() !== metric.code ? (
              <div className="text-xs" style={{ color: "var(--color-warning-text)" }}>
                Kod {metric.code} → {draft.code.trim()} olarak değişecek; bu koda atıf yapan tüm
                formüller (katalog + veri setleri) kaydederken otomatik güncellenir, onay istenir.
              </div>
            ) : (
              <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
                Küçük harf, rakam ve alt çizgi; formüllerde bu kodla anılır.
              </div>
            )}
          </div>
          <MetricModalLocation
            metric={metric}
            catalog={catalog}
            view={view}
            kind={props.kind ?? "metric"}
            draft={draft}
            mode={locMode}
            disabled={dis}
            rootAllowed={rootAllowed}
            set={set}
            onMode={setLocMode}
            onNewGroup={
              props.onNewGroup ? (id) => props.onNewGroup?.(id, draft, "location") : undefined
            }
          />
          {locationBlock && canEdit ? (
            <div className="text-xs mb-3" style={{ color: "var(--color-text-3)" }} role="status">
              {locationBlock}
            </div>
          ) : null}
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="m-desc">
              Açıklama
            </label>
            <textarea
              id="m-desc"
              rows={2}
              style={{ ...inputStyle, resize: "vertical" }}
              value={draft.description}
              disabled={dis}
              maxLength={2000}
              onChange={(e) => set("description", e.target.value)}
            />
          </div>
          {!isNew ? <MetricLabelsSection metricId={metric.id} canEdit={canEdit} /> : null}
          {isGroup && draft.breakdown && metric ? (
            <GroupMetaSection
              groupId={metric.id}
              value={draft.breakdown}
              original={initialDraft.breakdown}
              disabled={dis}
              onChange={(b) => set("breakdown", b)}
            />
          ) : null}
          {!isCategory && !memberMode && metric && metric.parentId !== null ? (
            <TotalBreakdownPanel
              metric={metric}
              view={view}
              disabled={dis}
              onAddGroup={
                props.onNewGroup ? (id) => props.onNewGroup?.(id, draft, "panel") : undefined
              }
            />
          ) : null}
          {isCategory && !isGroup ? (
            <div className="text-xs mb-3" style={{ color: "var(--color-text-3)" }}>
              Kategori bir başlıktır: altına gösterge eklenir, kendisine değer girilmez. Birim,
              formül ve standart eşlemesi göstergelerde tanımlanır.
            </div>
          ) : null}
          {isCategory ? null : (
            <>
              {memberMode ? (
                <div style={fieldStyle} data-testid="kpi-metric-unit-inherited">
                  <span style={labelStyle}>{L.unitLabel}</span>
                  <span style={{ ...inputStyle, background: "var(--color-bg)" }} aria-disabled>
                    {L.unitInherited(totalOfMember?.defaultUnit ?? "")}
                  </span>
                  <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                    {L.unitInheritedHint}
                  </span>
                </div>
              ) : (
                <MetricUnitSection
                  metricId={metric?.id ?? null}
                  originalDefaultUnit={metric?.defaultUnit ?? null}
                  defaultUnit={draft.defaultUnit}
                  unitVariants={draft.unitVariants}
                  isDerived={draft.isDerived}
                  disabled={dis}
                  onDefaultUnit={(v) => set("defaultUnit", v)}
                  onVariants={(v) => set("unitVariants", v)}
                  onBlockedChange={setUnitBlocked}
                  onOpenClient={(id, setId = null) =>
                    isDirty
                      ? setPendingClient({ clientId: id, setId })
                      : void navigate(kpiSetManagementHref(id, setId))
                  }
                />
              )}
              <div style={fieldStyle}>
                <label style={labelStyle} htmlFor="m-vtype">
                  Değer tipi
                </label>
                <select
                  id="m-vtype"
                  style={{ ...inputStyle, maxWidth: 260 }}
                  value={draft.valueType}
                  disabled={dis}
                  onChange={(e) => set("valueType", e.target.value as KpiValueType)}
                >
                  {KPI_VALUE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {VALUE_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle} htmlFor="m-agg">
                  Konsolide (grup müşteri)
                </label>
                <select
                  id="m-agg"
                  style={inputStyle}
                  value={draft.aggregation}
                  disabled={dis}
                  onChange={(e) => set("aggregation", e.target.value as KpiAggregation)}
                >
                  {KPI_AGGREGATIONS.map((a) => (
                    <option key={a} value={a}>
                      {AGGREGATION_LABELS[a]}
                    </option>
                  ))}
                </select>
                <AggregationHint value={draft.aggregation} isDerived={draft.isDerived} />
              </div>
              {draft.aggregation === "weighted_avg" ? (
                <AggregationWeightField
                  id="m-agg-weight"
                  value={draft.aggregationWeightMetricId ?? ""}
                  options={weightOptions}
                  missing={weightMissing}
                  disabled={dis}
                  onChange={(v) => set("aggregationWeightMetricId", v || null)}
                />
              ) : null}
              <MetricModalDefaults draft={draft} disabled={dis} set={set} />
              <MetricRequirementsField
                value={draft.requirements}
                disabled={dis}
                onChange={(v) => set("requirements", v)}
              />
            </>
          )}
          {error && !error.formulaId && !error.cyclePath ? (
            <div style={{ ...errorBox, marginBottom: 12 }} role="alert">
              <MetricErrorText error={error} />
            </div>
          ) : null}
        </div>
        {isCategory ? null : (
          <div style={{ minWidth: 0 }}>
            <div
              className="rounded-lg p-3"
              style={{ background: "var(--color-bg)", border: "1px solid var(--color-border)" }}
            >
              <div style={fieldStyle}>
                <label
                  className="flex items-center gap-2 text-sm"
                  style={{ color: "var(--color-text)", minHeight: 32 }}
                >
                  <input
                    type="checkbox"
                    checked={draft.isDerived}
                    disabled={dis}
                    onChange={(e) => set("isDerived", e.target.checked)}
                  />
                  Hesaplanan gösterge (ƒx — formülle türetilir)
                </label>
              </div>
              {draft.isDerived ? (
                <>
                  <FormulaEditor
                    formulas={draft.defaultFormulas}
                    activeFormulaId={activeFormulaId}
                    codes={allCodes}
                    unitsByCode={unitsByCode}
                    labelsByCode={labelsByCode}
                    childrenByCode={childrenByCode}
                    {...(metric ? { ownCode: metric.code } : {})}
                    scope="catalog"
                    disabled={dis}
                    serverError={error}
                    onChange={(formulas) => set("defaultFormulas", formulas)}
                  />
                  {!formulasOk ? (
                    <div className="text-xs mb-3" style={{ color: "var(--color-error)" }}>
                      Hesaplanan gösterge en az bir aktif formül ister.
                    </div>
                  ) : null}
                </>
              ) : null}
              {!draft.isDerived ? (
                <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
                  Ham veri göstergesi: değer elle ya da içe aktarmayla girilir. Formülle
                  türetilecekse yukarıdaki kutuyu işaretleyin — yazarken kod önerileri açılır.
                </div>
              ) : null}
            </div>
          </div>
        )}
      </form>

      <ConfirmDialog
        open={pendingClient !== null}
        title={KPI_COPY.unsavedClose.title}
        body={KPI_COPY.unsavedClose.body}
        confirmLabel={KPI_COPY.unsavedClose.confirm}
        cancelLabel={KPI_COPY.unsavedClose.cancel}
        tone="danger"
        onConfirm={() => {
          const target = pendingClient;
          setPendingClient(null);
          if (target) void navigate(kpiSetManagementHref(target.clientId, target.setId));
        }}
        onCancel={() => setPendingClient(null)}
      />
      <ConfirmDialog
        open={confirmDeprecate}
        title="Gösterge kullanımdan kaldırılsın mı?"
        body="Değeri ya da alt göstergesi varsa kullanımdan kalkar (değerler korunur, veri setlerinde görünür ama yeni veri setine eklenemez); hiç kullanılmamışsa katalogdan silinir."
        confirmLabel="Kullanımdan kaldır"
        tone="danger"
        busy={busy}
        onConfirm={() => {
          setConfirmDeprecate(false);
          onDeprecate();
        }}
        onCancel={() => setConfirmDeprecate(false)}
      />
    </KpiModal>
  );
}
